import React, { createContext, useContext, useEffect } from 'react';
import { BRAND_THEME_ID, colorSchemes } from './theme/colorSchemes';
import { buildThemeCssVariables } from './theme/buildThemeCssVariables';

const ThemeContext = createContext();

const LOCKED_THEME = BRAND_THEME_ID;
const LOCKED_DARK_MODE = false;

/**
 * Single professional brand theme for VLSI Expert Pvt. Ltd.
 * Theme switcher / dark mode are disabled; CSS variables still drive the UI.
 */
export const ThemeProvider = ({ children }) => {
  const currentTheme = LOCKED_THEME;
  const isDarkMode = LOCKED_DARK_MODE;

  const getCurrentScheme = () => colorSchemes[LOCKED_THEME];

  const getColor = (colorType, shade = 500) => {
    const scheme = getCurrentScheme();
    return scheme.colors[colorType]?.[shade] || scheme.colors.primary[500];
  };

  const getCSSVariables = () =>
    buildThemeCssVariables({
      scheme: getCurrentScheme(),
      currentTheme,
      isDarkMode,
      getColor,
    });

  useEffect(() => {
    // Clear legacy multi-theme preferences so they cannot reappear
    localStorage.removeItem('sms-theme');
    localStorage.removeItem('sms-dark-mode');

    const variables = getCSSVariables();
    const root = document.documentElement;
    const body = document.body;

    Object.entries(variables).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });

    body.className = body.className
      .split(/\s+/)
      .filter((c) => c && !c.startsWith('theme-') && c !== 'dark')
      .join(' ');
    body.classList.add(`theme-${LOCKED_THEME}`);
  }, []);

  const value = {
    currentTheme,
    isDarkMode,
    colorSchemes,
    getCurrentScheme,
    getColor,
    toggleDarkMode: () => {},
    changeTheme: () => {},
    getCSSVariables,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
